/**
 * BoardEntrance (BL-190) — the composed "first 10 s on the board" power-on beat.
 *
 * The boot → menu → pre-run chain is rich; then the arena just turned on
 * (UI_AUDIT §A/§3: "the board just appears"). This is the one absent hero beat —
 * a brief POWER-ON as the play surface lights up: a scan-in light wipe + the
 * column edges lifting to their lit chrome, landing inside the existing ~1.8 s
 * `enterCombatSweep` music riser at `main.ts` startRun (the "drop" into combat).
 *
 * It REUSES the established vocabulary: the BoardChrome scanline/gold-etch look
 * already on the columns (it lights THAT chrome, doesn't re-draw it), the
 * `UIChromeCanvas` glint (`settleIn` — UIChromeCanvas.ts:306, the audit's `:291`
 * reduced-motion seam; the literal "chrome glint"), and the central
 * `UI_EASE`/`UI_DURATION` tokens.
 *
 * ONE-TIME: a single overlay that self-removes on `animationend`, so it adds ZERO
 * steady-state peak-play density. Pure DOM/CSS (Layer 4) → determinism-neutral.
 * Reduced-motion → a complete no-op (the board just appears, fully lit). Colour
 * is central (`UI_CHROME`) — no new gameplay hex.
 */
import { isReducedMotion } from "../engine/MotionSettings.ts";
import { UI_CHROME, UI_EASE, UI_DURATION } from "./uiTokens.ts";
import type { UIChromeCanvas } from "./UIChromeCanvas.ts";

/** Total scan-wipe duration (ms). One unhurried left→right pass. */
const SCAN_MS = 820;
/** How long the whole overlay lingers before self-removal (covers the glint tail). */
const HOLD_MS = SCAN_MS + UI_DURATION.settle + 120;

let stylesInjected = false;

function ensureStyles(): void {
  if (stylesInjected || typeof document === "undefined") return;
  if (document.getElementById("board-entrance-styles")) {
    stylesInjected = true;
    return;
  }
  stylesInjected = true;
  const hi = UI_CHROME.greenHi; // central board highlight
  const style = document.createElement("style");
  style.id = "board-entrance-styles";
  // The overlay: a transient edge-light "coming online" (.be-glow) + a single
  // left→right scan-in wipe (.be-scan). pointer-events:none, self-removed; the
  // @media guards a future direct-mount caller (this fn already no-ops if reduced).
  style.textContent = `
    .board-entrance {
      position: absolute; inset: 0; z-index: 7;
      pointer-events: none; overflow: hidden;
    }
    .board-entrance .be-glow {
      position: absolute; inset: 0; opacity: 0;
      box-shadow:
        inset 0 0 60px color-mix(in srgb, ${hi} 14%, transparent),
        inset 0 0 2px color-mix(in srgb, ${hi} 30%, transparent);
      animation: be-edge-on ${UI_DURATION.flash}ms ${UI_EASE.settle} both;
    }
    .board-entrance .be-scan {
      position: absolute; top: 0; bottom: 0; width: 38%;
      background: linear-gradient(90deg,
        transparent,
        color-mix(in srgb, ${hi} 18%, transparent),
        transparent);
      mix-blend-mode: screen;
      animation: be-scan ${SCAN_MS}ms ${UI_EASE.decay} both;
    }
    @keyframes be-scan {
      0% { left: -40%; opacity: 0; } 14% { opacity: 1; } 100% { left: 100%; opacity: 0; }
    }
    @keyframes be-edge-on {
      0% { opacity: 0; } 45% { opacity: 1; } 100% { opacity: 0; }
    }
    @media (prefers-reduced-motion: reduce) {
      .board-entrance .be-glow,
      .board-entrance .be-scan { animation: none; opacity: 0; }
    }
  `;
  document.head.appendChild(style);
}

/**
 * Play the one-time board power-on beat over `boardRoot` (the 3-column frame,
 * e.g. `#main-area`) and fire a `UIChromeCanvas.settleIn` glint on the given lit
 * panels (the chrome's own emissive settle). Reduced-motion → a complete no-op
 * (nothing mounted, no glint; the board just appears fully lit). Idempotent: a
 * stale overlay from a prior run is removed first, so a restart can't stack two.
 */
export function playBoardEntrance(
  boardRoot: HTMLElement | null | undefined,
  opts: { chrome?: UIChromeCanvas; glintPanels?: HTMLElement[] } = {},
): void {
  if (typeof document === "undefined" || !boardRoot) return;
  // Always clear any in-flight overlay (a restart mid-entrance).
  for (const stale of boardRoot.querySelectorAll(".board-entrance")) {
    stale.remove();
  }
  // Reduced-motion: no beat at all. The board is already lit; just return.
  if (isReducedMotion()) return;

  ensureStyles();

  // The overlay is position:absolute; inset:0 → it needs a positioned ancestor.
  // The board root (#main-area) is a flex row with no position; establish a
  // containing block ONLY if it is currently static. This is layout-neutral —
  // the flex children stay in flow; the absolute, pointer-events:none overlay
  // does not participate — and mirrors how the side panels are already relative.
  if (
    typeof getComputedStyle === "function" &&
    getComputedStyle(boardRoot).position === "static"
  ) {
    boardRoot.style.position = "relative";
  }

  const overlay = document.createElement("div");
  overlay.className = "board-entrance";
  overlay.setAttribute("aria-hidden", "true");

  const glow = document.createElement("div");
  glow.className = "be-glow";
  const scan = document.createElement("div");
  scan.className = "be-scan";
  overlay.append(glow, scan);

  boardRoot.appendChild(overlay);

  // Self-remove on the scan's animationend (the longest child), with a timer
  // fallback for headless / interrupted-animation environments.
  let removed = false;
  const cleanup = (): void => {
    if (removed) return;
    removed = true;
    overlay.remove();
  };
  scan.addEventListener("animationend", cleanup, { once: true });
  if (typeof setTimeout !== "undefined") setTimeout(cleanup, HOLD_MS);

  // Reuse the panel chrome glint (the same settle the panels animate with). This
  // is itself a no-op under reduced-motion at the source (UIChromeCanvas:306),
  // but we've already returned above in that case.
  if (opts.chrome && opts.glintPanels) {
    for (const panel of opts.glintPanels) opts.chrome.settleIn(panel);
  }
}

/** Test/reset hook — drops the injected-style guard. */
export function __resetBoardEntranceForTest(): void {
  stylesInjected = false;
}
